import api from './api';


export interface WalletMember {
  id?: string;
  externalId?: string;
  tierId: string;
  programId: string;
  person: {
    surname?: string;
    forename?: string;
    emailAddress?: string;
    mobileNumber?: string;
    displayName?: string;
  };
  status?: string;
  points?: number;
  optOut?: boolean;
  created?: string;
  updated?: string;
}


export interface MemberListFilters {
  searchText?: string;
  pageSize?: number;
  page?: number;
  orderBy?: string;
  ascending?: boolean;
  filters?: {
    externalId?: { filterValue: string; filterOperator: string; };
    surname?: { filterValue: string; filterOperator: string; };
    forename?: { filterValue: string; filterOperator: string; };
    emailAddress?: { filterValue: string; filterOperator: string; };
    mobileNumber?: { filterValue: string; filterOperator: string; };
    status?: { filterValue: string; filterOperator: string; };
    tierId?: { filterValue: string; filterOperator: string; };
    groupingIdentifier?: { filterValue: string; filterOperator: string; };
    points?: { filterValue: number; filterOperator: string; };
    created?: { filterValue: string; filterOperator: string; };
    updated?: { filterValue: string; filterOperator: string; };
  };
}


export const walletMembersAPI = {
  listMembers: async (programId: string, filters?: MemberListFilters) => {
    // Use the new multi-tenant endpoint that fetches from PassKit
    const params: any = {};

    // Add program filter
    if (programId) {
      params.programId = programId;
    }

    // Add search filter
    if (filters?.searchText) {
      params.search = filters.searchText;
    }

    // Add tier filter
    if (filters?.filters?.tierId?.filterValue) {
      params.tierId = filters.filters.tierId.filterValue;
    }

    // Add pagination
    if (filters?.pageSize) {
      params.limit = filters.pageSize;
    }
    if (filters?.page) {
      params.skip = (filters.page - 1) * (filters.pageSize || 25);
    }

    const response = await api.get('/membership/members', { params });
    // Backend returns { members: [...], pagination: {...} }
    return response.data;
  },

  createMember: async (member: WalletMember) => {
    const response = await api.post('/membership/members', member);
    return response.data;
  },

  updateMember: async (member: WalletMember) => {
    // Use the new membership endpoint that includes member ID in URL
    if (!member.id) {
      throw new Error('Member ID is required for update');
    }
    const response = await api.patch(`/membership/members/${member.id}`, member);
    return response.data;
  },

  deleteMember: async (member: any) => {
    const memberId = member.id || member.externalId;
    if (!memberId) {
      throw new Error('Member ID is required for delete');
    }
    const programId = member.programId;
    if (!programId) {
      throw new Error('programId is required for delete');
    }
    await api.delete(`/membership/members/${memberId}`, { params: { program_id: programId } });
  },

  getTotalMembersCount: async (programId: string): Promise<number> => {
    const result = await api.post(`/passkit/members/count/${programId}`, {});
    return result.data.totalCount || 0;
  },

  countMembers: async (programId: string, filters?: any): Promise<{ totalCount: number }> => {
    const result = await api.post(`/passkit/members/count/${programId}`, filters || {});
    return { totalCount: result.data.totalCount || 0 };
  },

  getActiveMembersCount: async (programId: string): Promise<number> => {
    const result = await api.post(`/passkit/members/count/${programId}`, {
      filters: {
        status: {
          filterValue: 'ACTIVE',
          filterOperator: 'EQUAL'
        }
      }
    });
    return result.data.totalCount || 0;
  },

  bulkDeleteMembers: async (programId: string, memberIdentifiers: Array<{ id?: string; externalId?: string }>): Promise<{
    success: number;
    failed: number;
    errors: Array<{ identifier: any; error: string }>;
  }> => {
    const results = {
      success: 0,
      failed: 0,
      errors: [] as Array<{ identifier: any; error: string }>
    };

    for (const identifier of memberIdentifiers) {
      try {
        const memberId = identifier.id || identifier.externalId;
        if (!memberId) throw new Error('No member ID');
        await api.delete(`/membership/members/${memberId}`, {
          params: { program_id: programId }
        });
        results.success++;
      } catch (error: any) {
        results.failed++;
        results.errors.push({
          identifier,
          error: error.message || 'Unknown error'
        });
      }
    }

    return results;
  }
};

export default walletMembersAPI;
